import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Language, TranslationKeys } from './types';
import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES } from './types';
import en from './translations/en';
import id from './translations/id';
import fr from './translations/fr';
import zh_CN from './translations/zh_CN';
import zh_TW from './translations/zh_TW';
import es from './translations/es';
import th from './translations/th';

const translations: Record<Language, TranslationKeys> = { en, id, fr, zh_CN, zh_TW, es, th };

type Dict = Record<string, string | number>;

function interpolate(str: string, params?: Dict): string {
  if (!params) return str;
  return str.replace(/\{(\w+)\}/g, (_, key: string) =>
    key in params ? String(params[key]) : `{${key}}`
  );
}

function resolvePath(obj: any, path: string): any {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

export function createT(lang: Language) {
  const dict = translations[lang] ?? translations.en;
  return function t(path: string, params?: Dict): string {
    const value = resolvePath(dict, path);
    if (typeof value === 'string') return interpolate(value, params);
    const fallback = resolvePath(translations.en, path);
    if (typeof fallback === 'string') return interpolate(fallback, params);
    return path;
  };
}

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: ReturnType<typeof createT>;
  languageNames: Record<Language, string>;
  supportedLanguages: Language[];
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function getInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'en';
  const stored = localStorage.getItem('language') as Language | null;
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
  const nav = navigator.language.toLowerCase();
  if (nav.startsWith('zh')) {
    const region = navigator.language.toUpperCase();
    return region === 'ZH-TW' || region === 'ZH-HK' ? 'zh_TW' : 'zh_CN';
  }
  if (nav.startsWith('id')) return 'id';
  if (nav.startsWith('fr')) return 'fr';
  if (nav.startsWith('es')) return 'es';
  if (nav.startsWith('th')) return 'th';
  return 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language === 'zh_CN' ? 'zh-CN' : language === 'zh_TW' ? 'zh-TW' : language;
  }, [language]);

  const value = useMemo<LanguageContextValue>(() => ({
    language,
    setLanguage: setLanguageState,
    t: createT(language),
    languageNames: LANGUAGE_NAMES,
    supportedLanguages: SUPPORTED_LANGUAGES,
  }), [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}